import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { getFileSizeString } from "@msuite/utils/strings";
import type { FileBatchListItem } from "@msuite/utils/types";
import { FileOutcome } from "@/components/files";
import { RowActions } from "./row-actions";

const columnHelper = createColumnHelper<FileBatchListItem>();

/** Options Interface */
interface CreateColumnsOptions {
	/** Handed to the row menu, for the one action that navigates out from under the report. */
	onNavigate: () => void;
}

/**
 * The report's columns, and deliberately the inbox's minus the ones a drop already answers.
 *
 * Dropped-at and dropped-by are gone: every row of one batch shares both, and a column repeating
 * the same value down the page is a column the reader has to look past. What is left is the file,
 * what became of it, and — on the rows nothing became of — the menu that settles it.
 *
 * A factory rather than a constant, because the row menu has to be able to close the report it is
 * rendered in.
 *
 * @param options - The report's own callbacks, forwarded to the row menu.
 * @returns The column definitions for the batch table.
 */
export function createColumns({ onNavigate }: CreateColumnsOptions) {
	return [
		columnHelper.accessor((row) => row.file.fileName, {
			id: "file-name",
			header: localized("files.file_name"),
			cell: (cell) => cell.getValue(),
			size: 300,
		}),
		columnHelper.accessor((row) => row.file.size, {
			id: "size",
			header: localized("files.file_size"),
			cell: (cell) => getFileSizeString(cell.getValue()),
			size: 110,
		}),
		// The same component the inbox renders, so a file that ends up on both pages reads alike.
		columnHelper.accessor("state", {
			id: "outcome",
			header: localized("files.outcome"),
			cell: (cell) => (
				<FileOutcome
					state={cell.row.original.state}
					reason={cell.row.original.reason}
					error={cell.row.original.error}
					target={cell.row.original.target}
				/>
			),
			size: 520,
		}),
		createRowActions<FileBatchListItem>((props) => (
			<RowActions
				{...props}
				onNavigate={onNavigate}
			/>
		)),
	];
}
